// Spot Healing Brush tool (J) — automatically samples from surrounding pixels
// to fix blemishes. No Alt+click needed; it samples from a ring around the brush.
//
// Algorithm:
// 1. For each stroke, first collect surrounding pixel colors (a ring around the brush)
// 2. Compute the average color of the ring
// 3. For each pixel in the brush area, blend the source texture with the ring average
//    to match the surrounding tone

import { HealStrokeCommand, snapshotRegion } from "../commands/HealStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
}

export class SpotHealingTool implements Tool {
  readonly id = "spot-healing";
  readonly shortcut = "J";

  settings: BrushSettings = { size: 20, hardness: 0.5, opacity: 1, flow: 1 };
  sampleMode: "current" | "below" | "all" = "current";

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    selMask: { mask: Uint8Array | undefined; w: number | undefined };
  } | null = null;

  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    if (!ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      selMask: this.getSelMask(ctx),
    };

    this.heal(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = Math.max(1, this.settings.size / 4);
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.heal(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new HealStrokeCommand(layer.id, bbox, before, after));
  }

  private heal(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active) return;
    const dstLayer = this.active.layer;
    const radius = this.settings.size / 2;
    const hardness = this.settings.hardness;
    const opacity = this.settings.flow;
    const srcData = this.active.layerSnapshot;
    const dstData = dstLayer.pixels.data;
    const w = dstLayer.pixels.width;
    const h = dstLayer.pixels.height;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));
    const { mask: selMask, w: selMaskW } = this.active.selMask;
    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    const ringAvg = this.getRingAverage(srcData, w, h, docX, docY, radius);

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          if (selVal === 0) continue;
        }

        const dstIdx = (py * w + px) * 4;

        if (dstLayer.lockTransparency && dstData[dstIdx + 3] === 0) continue;

        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const alpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        let blend = alpha * opacity;

        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          blend *= selVal / 255;
        }

        if (blend <= 0) continue;

        const srcR = srcData[dstIdx]!;
        const srcG = srcData[dstIdx + 1]!;
        const srcB = srcData[dstIdx + 2]!;

        const destAvg = this.getLocalAverage(dstData, w, h, px, py, Math.max(3, Math.floor(radius * 0.5)));

        const diffR = destAvg.r - ringAvg.r;
        const diffG = destAvg.g - ringAvg.g;
        const diffB = destAvg.b - ringAvg.b;

        const healedR = clampByte(srcR + diffR);
        const healedG = clampByte(srcG + diffG);
        const healedB = clampByte(srcB + diffB);

        dstData[dstIdx] = Math.round(dstData[dstIdx]! * (1 - blend) + healedR * blend);
        dstData[dstIdx + 1] = Math.round(dstData[dstIdx + 1]! * (1 - blend) + healedG * blend);
        dstData[dstIdx + 2] = Math.round(dstData[dstIdx + 2]! * (1 - blend) + healedB * blend);

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    dstLayer.pixels.markDirty();
  }

  private getRingAverage(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    cx: number,
    cy: number,
    innerRadius: number,
  ): { r: number; g: number; b: number } {
    const outerRadius = innerRadius + 4;
    let rSum = 0, gSum = 0, bSum = 0, count = 0;
    const innerR2 = innerRadius * innerRadius;
    const outerR2 = outerRadius * outerRadius;
    const minX = Math.max(0, Math.floor(cx - outerRadius));
    const minY = Math.max(0, Math.floor(cy - outerRadius));
    const maxX = Math.min(w - 1, Math.ceil(cx + outerRadius));
    const maxY = Math.min(h - 1, Math.ceil(cy + outerRadius));

    for (let py = minY; py <= maxY; py++) {
      const dy = py - cy;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px - cx;
        const distSq = dx * dx + dy2;
        if (distSq < innerR2 || distSq > outerR2) continue;
        const idx = (py * w + px) * 4;
        const weight = 1 - (Math.sqrt(distSq) - innerRadius) / (outerRadius - innerRadius);
        rSum += data[idx]! * weight;
        gSum += data[idx + 1]! * weight;
        bSum += data[idx + 2]! * weight;
        count += weight;
      }
    }

    if (count === 0) return { r: 128, g: 128, b: 128 };
    return { r: rSum / count, g: gSum / count, b: bSum / count };
  }

  private getLocalAverage(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    cx: number,
    cy: number,
    radius: number,
  ): { r: number; g: number; b: number } {
    let rSum = 0, gSum = 0, bSum = 0, count = 0;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    for (let py = minY; py <= maxY; py++) {
      const dy = py - cy;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px - cx;
        if (dx * dx + dy2 > r2) continue;
        const idx = (py * w + px) * 4;
        const weight = 1 - Math.sqrt(dx * dx + dy2) / (radius + 1);
        rSum += data[idx]! * weight;
        gSum += data[idx + 1]! * weight;
        bSum += data[idx + 2]! * weight;
        count += weight;
      }
    }

    if (count === 0) return { r: 128, g: 128, b: 128 };
    return { r: rSum / count, g: gSum / count, b: bSum / count };
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}
